import { types, getParent, getRoot, flow } from 'mobx-state-tree';
import _ from 'lodash';
import {
  Plan,
  Payment,
  SubscriptionData,
  AddOn,
  GoodbyeAnswers,
} from '../../models/SettingsStore/Subscription';
import downloadFile from '../../utils/downloadFile';

const BASE_URL = '/subscription/v1';
const BASE_URL_2 = '/account/v1';
// const PAYPAL_SERVICE_URL = '/paypal-api-service/v1'; // /customer

const poppedCardAddOnsToUpdate = types.model('poppedCardAddOnsToUpdate', {
  cost: types.optional(types.number, 0),
  id: types.optional(types.string, ''),
  isSelected: types.optional(types.boolean, false),
  name: types.optional(types.string, ''),
  plans: types.optional(types.string, ''),
  typeId: types.optional(types.string, ''),
});

const payoneerUpgrade = types.model('payoneerUpgrade', {
  wantUpgrade: types.optional(types.boolean, false),
  planId: types.optional(types.string, ''),
  poppedCardAddOns: types.optional(types.array(poppedCardAddOnsToUpdate), []),
});

const stripeUpgrade = types.model('stripeUpgrade', {
  wantUpgrade: types.optional(types.boolean, false),
  planId: types.optional(types.string, ''),
  poppedCardAddOns: types.optional(types.array(poppedCardAddOnsToUpdate), []),
});

const Subscription = types
  .model('subscription', {
    payments: types.optional(types.array(Payment), []),
    plans: types.optional(types.array(Plan), []),
    addOns: types.optional(types.array(AddOn), []),
    subscriptionData: types.optional(SubscriptionData, {}),
    goodbyeAnswers: types.optional(GoodbyeAnswers, {}),
    billingPaypalAccountId: types.maybe(types.string),
    isPaypalPreferred: types.optional(types.boolean, false),
    billingPayoneerAccountId: types.maybe(types.string),
    isPayoneerPreferred: types.maybe(types.boolean, false),
    billingStripeAccountId: types.maybe(types.string),
    isStripePreferred: types.maybe(types.boolean, false),
    payoneerUpgradePlan: types.optional(payoneerUpgrade, {}),
    stripeUpgradePlan: types.optional(stripeUpgrade, {}),
    userHasCourse: types.maybe(types.boolean, false),
    hasCourseDiscount: types.maybe(types.boolean, false),
    loading: false,
    sdInitialized: false,
    pInitialized: false,
    BIFMCatalogOn: true,
    canCancel: false,
    trialEligible: types.optional(types.boolean, false),
    isEligiblePromotion1000Plan: types.maybe(types.boolean, false),
    is10kPromotionEnding: types.optional(types.boolean, false),
    isFirstPaidSubscription: types.optional(types.boolean, false),
    isStripeDisabled: true,
    isPaypalDisabled:true,
    isCourseModalOpen:false,
  })
  .views(self => ({
    get sortedPayments() {
      return _.sortBy(self.payments, ({ paymentDatetime }) => {
        return -new Date(paymentDatetime).getTime();
      });
    },
    get activeAddOnsMap() {
      return Object.fromEntries(self.subscriptionData.addOns.map(addOnId => [addOnId, true]));
    },
    get transport5007() {
      return getParent(self).transport5007;
    },
    get accountCode() {
      return getRoot(self).uiStore.sessionStore.accountCode;
    },
    get isBifmUser() {
      return getRoot(self).uiStore.sessionStore.isBifmUser;
    },
    get accountListedItemsCount(){
      return getRoot(self).uiStore?.dashboardStore?.miniDashboard?.listings
    },
    get currentPlan() {
      return self.plans.find(plan => plan.id === self.subscriptionData.subscriptionPlanId);
    },
    get currentPlanId() {
      return self.subscriptionData.subscriptionPlanId;
    },
    get currentPlanFree() {
      return self.currentPlanId === 'free';
    },
    get hasDoubleLimitsAddOn() {
      const addOns = self.activeAddOnsMap;
      const hasDoubleLimitsAddOn = addOns.holiday !== null && addOns.holiday === true;
      return hasDoubleLimitsAddOn;
    },
    get usagePercents() {
      const multiplyLimits = self.hasDoubleLimitsAddOn ? 2 : 1;

      if (self.currentPlan) {
        return {
          autoPurchaseUsage:
            (self.subscriptionData.accountAutopurchaseRunsCount /
              (self.currentPlan.maxAutopurchases * multiplyLimits)) *
            100,
          listedItemsUsage:
            (self.accountListedItemsCount /
              (self.currentPlan.maxListedItems * multiplyLimits)) *
            100,
        };
      }
      return {
        autoPurchaseUsage: 0,
        listedItemsUsage: 0,
      };
    },
    get email() {
      return getParent(self).userProfile.email;
    },
    get showToast() {
      return getParent(self, 2).toastsStore.showToast;
    },
    get enums() {
      return getParent(self, 2).enumsStore.getEnumsList;
    },
    get billingPaypalAccount() {
      return getParent(self).connectionManagement.getAccountById(self.billingPaypalAccountId);
    },
    get billingPayoneerAccount() {
      return getParent(self).connectionManagement.getPayoneerAccountById(
        self.billingPayoneerAccountId,
      );
    },
    get billingStripeAccount() {
      return getParent(self).connectionManagement.getStripeAccountById(self.billingStripeAccountId);
    },
    // the select will have id as key and name as value
    get billableAccounts() {
      return getParent(self).connectionManagement.billableAccounts;
    },
    get billablePayoneerAccounts() {
      return getParent(self).connectionManagement.billablePayoneerAccounts;
    },
    get billableStripeAccounts() {
      return getParent(self).connectionManagement.billableStripeAccounts;
    },
    get trialExpiredNoSubscription() {
      return !self.subscriptionData.subscriptionPlanId && !self.payments.length;
    },
    get subscriptionInitialized() {
      return self.sdInitialized && self.pInitialized;
    },
    get activePlan() {
      return self.planById(self.subscriptionData.subscriptionPlanId);
    },
    get displayedPlans() {
      let filteredPlans= self.plans.filter(pl => !!pl.includesBIFMCatalog === self.BIFMCatalogOn);
      if(self.isEligiblePromotion1000Plan){
        filteredPlans=filteredPlans.filter(pl => pl.maxListedItems > 999)
        const plan1kPromotion=self.plans.filter(x => x.id === 'plan_1000_e');
        const index=filteredPlans.findIndex(x=>x.id==='plan_1000');
        if(plan1kPromotion.length > 0){
        // eslint-disable-next-line prefer-destructuring
          filteredPlans[index]=plan1kPromotion[0];
        }
      }

      return filteredPlans;
    },
    get catalogAvailable() {
      return !!self?.currentPlan?.includesBIFMCatalog ?? false;
    },
    get onboardingFlow() {
      return getParent(self, 2).onboardingStore.flow;
    },
    get userProfile() {
      return getParent(self).userProfile;
    },
    get firstPlanIndexOnboarding() {
      if (self.onboardingFlow === 'beginner') {
        return 0;
        // TO DO: SET HERE CORRECT INDEX AFTER DECIDE THE NAMING
      }
      return 0;
    },
    get displayedPlansOnboarding() {
      let filteredPlans= self.plans.filter(pl => !!pl.includesBIFMCatalog);
      if(self.isEligiblePromotion1000Plan){
        filteredPlans=filteredPlans.filter(pl => pl.maxListedItems > 999)
        const plan1kPromotion=self.plans.filter(x => x.id === 'plan_1000_e');
        const index=filteredPlans.findIndex(x=>x.id==='plan_1000');
        if(plan1kPromotion.length > 0){
        // eslint-disable-next-line prefer-destructuring
          filteredPlans[index]=plan1kPromotion[0];
        }
      }

      return filteredPlans;
    },
  }))
  .actions(self => ({
    setWantUpgrade: value => {
      self.payoneerUpgradePlan.wantUpgrade = value;
    },
    setWantUpgradeWithStripe: value => {
      self.stripeUpgradePlan.wantUpgrade = value;
    },
    setIsCourseModalOpen: value=>{
      self.isCourseModalOpen=value;
    },
    setPlanId: value => {
      self.payoneerUpgradePlan.planId = value;
    },
    setPlanIdWithStripe: value => {
      self.stripeUpgradePlan.planId = value;
    },
    setPoppedCardAddOns: value => {
      self.payoneerUpgradePlan.poppedCardAddOns = value;
    },
    setPoppedCardAddOnsWithStripe: value => {
      self.stripeUpgradePlan.poppedCardAddOns = value;
    },
    planById(id) {
      return self.plans.find(el => el.id === id);
    },
    addOnById(id) {
      return self.addOns.find(ao => ao.hasPlan(id));
    },
    // fetchTrialEligibility: flow(function* fetchTrialEligibility() {
    //   try {
    //     const URL = `${BASE_URL}/subscriptions/eligible-one-dollar`;
    //     const {
    //       data: { eligible },
    //     } = yield self.transport5007.fetch(URL, {}, {});
    //     self.trialEligible = eligible;
    //   } catch (error) {
    //     console.log('fetchTrialEligibity fail: ', error);
    //   }
    // }),
    fetchPaypalPaymentEligibility: flow(function* fetchTrialEligibility() {
      try {
        const URL = `${BASE_URL}/subscriptions/eligible-paypal-payment`;
        const {
          data: { eligible },
        } = yield self.transport5007.fetch(URL, {}, {});
        self.isFirstPaidSubscription = eligible;
      } catch (error) {
        console.log('fetchPaypalPaymentEligibility fail: ', error);
      }
    }),
    fetchBillingPaypalAccount: flow(function* fetchBillingPaypalAccount() {
      if (self.isBifmUser) return;
      try {
        const URL = `${BASE_URL}/payments/client-billing-paypal-account`;
        const {
          data: { isPreferred, accountId },
        } = yield self.transport5007.fetch(URL, {}, {});
        self.billingPaypalAccountId = accountId;
        self.isPaypalPreferred = isPreferred;
      } catch (error) {
        console.log('fetchSubscription fail: ', error);
      }
    }),
    fetchBillingPayoneerAccount: flow(function* fetchBillingPayoneerAccount() {
      if (self.isBifmUser) return;
      try {
        const URL = `${BASE_URL}/payments/client-billing-payoneer-account`;
        const {
          data: { isPreferred, accountId },
        } = yield self.transport5007.fetch(URL, {}, {});
        self.billingPayoneerAccountId = accountId;
        self.isPayoneerPreferred = isPreferred;
      } catch (error) {
        console.log('fetchSubscription fail: ', error);
      }
    }),
    fetchBillingStripeAccount: flow(function* fetchBillingStripeAccount() {
      if (self.isBifmUser) return;
      try {
        const URL = `${BASE_URL}/payments/client-billing-stripe-account`;
        const {
          data: { isPreferred, accountId },
        } = yield self.transport5007.fetch(URL, {}, {});
        self.billingStripeAccountId = accountId;
        self.isStripePreferred = isPreferred;
      } catch (error) {
        console.log('fetchSubscription fail: ', error);
      }
    }),
    // method below isn't used anymore ↓
    sendEmailToSupport: flow(function* sendEmailToSupport() {
      try {
        const URL = `${BASE_URL}/subscriptions/custom`;
        self.loading = true;
        yield self.transport5007.post(URL, { email: self.email }, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Your request was submitted, our sales representative will contact you shortly',
        });
      } catch (err) {
        console.error('sending mail failed: ', err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error contacting support',
        });
      } finally {
        self.loading = false;
      }
    }),
    fetchSubscription: flow(function* fetchSubscription() {
      if (self.isBifmUser) return;
      try {
        const URL = `${BASE_URL}/subscriptions`;
        const { data } = yield self.transport5007.fetch(URL, {}, {});
        data.subscriptionPlanId = data.subscriptionPlanId !== null ? data.subscriptionPlanId : '';
        self.subscriptionData = data;
      } catch (error) {
        console.log('fetchSubscription fail: ', error);
      } finally {
        self.sdInitialized = true;
      }
    }),
    fetchPayments: flow(function* fetchPayments() {
      try {
        const URL = `${BASE_URL}/Payments`;
        const { data } = yield self.transport5007.fetch(URL, {}, {});
        self.payments = data;
      } catch (err) {
        console.log(err);
      } finally {
        self.pInitialized = true;
      }
    }),
    setPlansEmpty() {
      self.plans = [];
    },
    fetchPlans: flow(function* fetchPlans() {
      try {
        const URL = `${BASE_URL}/plans`;
        const { data } = yield self.transport5007.fetch(URL, {}, {});
        self.plans = data;
      } catch (err) {
        console.log(err);
      }
    }),
    fetchAddOns: flow(function* fetchAddOns() {
      try {
        const { data } = yield self.transport5007.fetch(`${BASE_URL}/Subscriptions/addons`);
        self.addOns = data;
      } catch (err) {
        console.log(err);
      }
    }),
    fetchInvoice: flow(function* fetchInvoice(id) {
      try {
        const {
          data,
          headers: { 'content-disposition': contentDisposition },
        } = yield self.transport5007.fetch(`${BASE_URL}/Payments/invoice/${id}`, {}, {}, 'blob');
        const fileTitle = /filename=([\w.]+);/.exec(contentDisposition)[1];
        downloadFile(data, fileTitle, 'application/pdf');
      } catch (err) {
        console.error(err);
      }
    }),
    cancelSubscription: flow(function* cancelSubscription() {
      try {
        if (self.currentPlanId) {
          yield self.transport5007.update(`${BASE_URL}/Subscriptions`, { cancel: true }, {}, {});
          self.fetchSubscription();
        }
      } catch (err) {
        console.log('upgrading plan failed: ', err);
      }
    }),
    upgradePlan: flow(function* upgradePlan(id, addOns = [],purchaseId) {
      try {
        self.loading = true;
        const previousPlanConversionValue = self.currentPlan?.monthlyCost;
        const params = {
          plan: id,
          addOnsChanges: addOns
            .reduce((acc, curr) => (curr.isSelected ? [...acc, curr] : acc), [])
            .map(addOn => ({
              typeId: addOn.typeId,
              newAddOnPlanId: addOn.isSelected ? addOn.id : null,
            })),
          purchaseId
        };
        yield self.transport5007.update(`${BASE_URL}/Subscriptions`, params, {}, {});
        // yield self.fetchTrialEligibility();
        const purchase_value = self.plans.find(plan => plan.id === id)?.monthlyCost ?? 0;
        getParent(self).userProfile.ldRecordPurchase(purchase_value);
        self.setSubscriptionPlanId(id);
        params.addOnsChanges.forEach(addOn => self.addAddOn(addOn.newAddOnPlanId));
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Subscription updated successfully',
        });
        self.userProfile.updateOnboardingLevel('done onboarding');
        self.checkUserHasCourse();
        self.sendEventToGoogleTagManager(
          previousPlanConversionValue,
          purchase_value,
          true,
          params.plan,
          params.addOnsChanges,
        );
      } catch (err) {
        console.error('upgrading plan failed: ', err);
        const previousPlanConversionValue = self.currentPlan?.monthlyCost;
        const purchase_value = self.plans.find(plan => plan.id === id)?.monthlyCost ?? 0;
        const params = {
          plan: id,
          addOnsChanges: addOns
            .reduce((acc, curr) => (curr.isSelected ? [...acc, curr] : acc), [])
            .map(addOn => ({
              typeId: addOn.typeId,
              newAddOnPlanId: addOn.isSelected ? addOn.id : null,
            })),
        };
        self.sendEventToGoogleTagManager(
          previousPlanConversionValue,
          purchase_value,
          false,
          params.plan,
          params.addOnsChanges,
        );
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error updating the subscription',
        });
      } finally {
        self.loading = false;
      }
    }),
    updateAddOns: flow(function* updateAddOns(addOnsChanges) {
      try {
        self.loading = true;
        const params = {
          addOnsChanges,
        };
        yield self.transport5007.post(`${BASE_URL}/Subscriptions/addons`, params);
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Subscription Add-Ons updated successfully',
        });
        return true;
      } catch (err) {
        console.error('upgrading plan failed: ', err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error updating the Add-Ons',
        });
        return false;
      } finally {
        self.loading = false;
      }
    }),
    checkEligible1000PlanPromotion: flow(function* checkEligible1000PlanPromotion() {
      try {
        const { data } = yield self.transport5007.fetch(`${BASE_URL}/Subscriptions/is-eligible-1000-promotion`);
        self.isEligiblePromotion1000Plan=data ?? false;
      } catch (err) {
        console.log(err);
      }
    }),
    check10kPromotionIsEnding: flow(function* check10kPromotionIsEnding() {
      try {
        const { data } = yield self.transport5007.fetch(`${BASE_URL}/Subscriptions/is-10k-promotion-ending`);
        self.is10kPromotionEnding=data;
      } catch (err) {
        console.log(err);
      }
    }),
    checkUserHasCourse: flow(function* checkUserHasCourse() {
      try {
        const { data } = yield self.transport5007.fetch(`${BASE_URL}/Subscriptions/user-has-course`);
        self.userHasCourse=data ?? false;
      } catch (err) {
        console.log(err);
      }
    }),
    checkCouponCode: flow(function* checkCouponCode(couponCode) {
      try {
        const { data } = yield self.transport5007.fetch(`${BASE_URL}/Subscriptions/validate-coupon?code=${couponCode}`);
        self.hasCourseDiscount=data ?? false;

        if(!self.hasCourseDiscount){
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message:
              'Invalid coupone code!',
          });
        }
      } catch (err) {
        console.log(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message:
            'Something went wrong!',
        });
      }
    }),
    purchaseCourse: flow(function* purchaseCourse(paymentMethod,orderId,discountAmount) {
      try {
        yield self.transport5007.post(`${BASE_URL}/Subscriptions/purchase-course`,{
          paymentMethod,
          orderId,
          discountAmount
        });
        return true;
      } catch (err) {
        console.log(err);
        return false;
      }
    }),
    addHolidayFree: flow(function* addHolidayFree() {
      try {
        const data = yield self.transport5007.post(`${BASE_URL}/Subscriptions/holiday`);
        return data;
      } catch (err) {
        console.log(err);
        return null;
      }
    }),
    setSubscriptionPlanId: id => {
      self.subscriptionData.subscriptionPlanId = id;
    },
    setAddOns: ids => {
      self.subscriptionData.addOns = ids;
    },
    addAddOn: id => {
      self.subscriptionData.addOns.push(id);
    },
    removeAddOn: id => {
      const idx = self.subscriptionData.addOns.findIndex(addOn => addOn === id);
      if (idx !== -1) {
        self.subscriptionData.addOns.splice(idx, 1);
      }
    },
    toggleAddOn: (val, id) => {
      if (val) {
        self.addAddOn(id);
      } else {
        self.removeAddOn(id);
      }
    },
    initSubscription: flow(function* initSubscription() {
      yield Promise.allSettled([
        self.check10kPromotionIsEnding(),
        self.fetchBillingPaypalAccount(),
        self.fetchBillingPayoneerAccount(),
        self.fetchBillingStripeAccount(),
        self.fetchPlans(),
        self.fetchAddOns(),
        self.fetchSubscription(),
        self.fetchPayments(),
        self.fetchCancelStatus(),
        self.checkUserHasCourse(),
        self.checkEligible1000PlanPromotion(),
        self.fetchPaypalPaymentEligibility(),
        // self.fetchTrialEligibility(),
      ]);
    }),
    setBillingPaypalAccount: value => {
      if (typeof value === 'string') {
        self.billingPaypalAccountId = value;
      }
    },
    setBillingPayoneerAccount: value => {
      if (typeof value === 'string') {
        self.billingPayoneerAccountId = value;
      }
    },
    setBillingStripeAccount: value => {
      if (typeof value === 'string') {
        self.billingStripeAccountId = value;
      }
    },
    updateBillingPaypalAccount: flow(function* updateBillingPaypalAccount(value, isPreferred) {
      try {
        const URL = `${BASE_URL}/payments/billing-paypal-account`;
        yield self.transport5007.post(
          URL,
          {
            paypalAccountId: value,
            isPreferred,
          },
          {},
          {},
        );
        self.billingPaypalAccountId = value;
      } catch (err) {
        console.error('setting paypal account failed: ', err);
      }
    }),
    updateBillingPayoneerAccount: flow(function* updateBillingPayoneerAccount(value, isPreferred) {
      try {
        const URL = `${BASE_URL}/payments/billing-payoneer-account`;
        yield self.transport5007.post(
          URL,
          {
            payoneerAccountId: value,
            isPreferred,
          },
          {},
          {},
        );
        self.billingPayoneerAccountId = value;
      } catch (err) {
        console.error('setting payonner account failed: ', err);
      }
    }),
    updateBillingStripeAccount: flow(function* updateBillingStripeAccount(value, isPreferred) {
      try {
        const URL = `${BASE_URL}/payments/billing-stripe-account`;
        yield self.transport5007.post(
          URL,
          {
            stripeAccountId: value,
            isPreferred,
          },
          {},
          {},
        );
        self.billingStripeAccountId = value;
      } catch (err) {
        console.error('setting stripe account failed: ', err);
      }
    }),
    sendGoodbyeAnswers: flow(function* sendGoodbyeAnswers() {
      try {
        const URL = `${BASE_URL_2}/abandoning-users/add-abandoning-users`;
        self.loading = true;
        yield self.transport5007.post(
          URL,
          {
            ...self.goodbyeAnswers,
            accountId: self.accountCode,
            TMPlan: self.currentPlanId || '',
          },
          {},
          {},
        );
        self.canCancel = false;
        self.loading = false;
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message:
            'Your request was sent successfully and it will be processed by the support team!',
        });
      } catch (err) {
        console.error('sending cancel/close request failed: ', err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error submitting your request.',
        });
        self.loading = false;
      }
    }),
    fetchCancelStatus: flow(function* fetchCancelStatus() {
      try {
        const URL = `${BASE_URL_2}/abandoning-users/can-cancel`;
        self.loading = true;
        const { data } = yield self.transport5007.fetch(URL);
        self.canCancel = data;
      } catch (err) {
        console.error('getting cancel/close request failed: ', err);
      } finally {
        self.loading = false;
      }
    }),
    setBIFMCatalogOn(val) {
      self.BIFMCatalogOn = val;
    },
    sendEventToGoogleTagManager(
      previousPlanConversionValue,
      conversionValue,
      successful,
      planName,
      addOns,
    ) {
      let action = previousPlanConversionValue < conversionValue ? 'upgrade' : 'downgrade';
      if (!previousPlanConversionValue) action = 'new';

      window.dataLayer.push({
        event: 'plan-purchase',
        action,
        conversionValue,
        label: successful ? 'success' : 'failure',
        userId: getParent(self).userProfile.email,
        subscriptionPlanName: planName,
        planAddOns: addOns,
      });
    },
  }));

export default Subscription;
